/**
 * User Selection Dialog for adding members to groups
 * Allows searching and selecting users to add to a group
 *
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import React, { useState, useMemo } from 'react';
import {
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Box,
  Typography,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ModeratorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../ui/BaseDialog';
import { useUsers } from '../../hooks/api/useUsers';
import { log } from '../../utils/errors/logger';

interface UserSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedUsers: Array<{ userId: string; role: string }>) => void;
  excludeUserIds?: string[];
  groupName?: string;
  isLoading?: boolean;
}

interface UserWithSelection {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  selected: boolean;
  memberRole: string;
}

const getRoleIcon = (role?: string) => {
  switch (role) {
    case 'admin':
      return <AdminIcon />;
    case 'moderator':
      return <ModeratorIcon />;
    default:
      return <PersonIcon />;
  }
};

const getRoleColor = (role?: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'moderator':
      return 'warning';
    default:
      return 'default';
  }
};

export const UserSelectionDialog: React.FC<UserSelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  excludeUserIds = [],
  groupName,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Map<string, string>>(new Map());
  
  // Fetch all users
  const { data: usersResponse, isLoading: usersLoading } = useUsers(undefined, { enabled: open });
  const users = usersResponse?.users || [];

  // Filter and prepare users
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    
    return users
      .filter(user => !excludeUserIds.includes(user.id || user.username))
      .filter(user => {
        if (!query) {return true;}
        return (
          user.username?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.full_name?.toLowerCase().includes(query)
        );
      })
      .map(user => ({
        ...user,
        id: user.id || user.username,
        selected: selectedUsers.has(user.id || user.username),
        memberRole: selectedUsers.get(user.id || user.username) || 'member',
      }));
  }, [users, searchQuery, excludeUserIds, selectedUsers]);

  const handleToggleUser = (userId: string) => {
    setSelectedUsers(prev => {
      const newMap = new Map(prev);
      if (newMap.has(userId)) {
        newMap.delete(userId);
      } else {
        newMap.set(userId, 'member');
      }
      return newMap;
    });
    log.user.action('toggle_user_selection', 'UserSelectionDialog', { userId });
  };

  const handleChangeRole = (userId: string, role: string) => {
    setSelectedUsers(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, role);
      return newMap;
    });
    log.user.action('change_member_role', 'UserSelectionDialog', { userId, role });
  };

  const handleConfirm = () => {
    const usersToAdd = Array.from(selectedUsers.entries()).map(([userId, role]) => ({
      userId,
      role,
    }));
    onConfirm(usersToAdd);
    log.user.action('confirm_user_selection', 'UserSelectionDialog', { count: usersToAdd.length });
  };

  const handleClose = () => {
    setSelectedUsers(new Map());
    setSearchQuery('');
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={groupName
        ? t('groups.members.addToGroup', { groupName })
        : t('groups.members.selectUsers')
      }
      maxWidth="sm"
      fullWidth
      loading={isLoading}
      DialogProps={{ style: { zIndex: 9999 } }}
      actions={
        <>
          <Button onClick={handleClose} startIcon={<CloseIcon />}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={selectedUsers.size === 0 || isLoading}
          >
            {isLoading
              ? t('groups.members.adding')
              : t('groups.members.addSelected', { count: selectedUsers.size })
            }
          </Button>
        </>
      }
    >
      {/* Title bar with selection count */}
      {selectedUsers.size > 0 && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Chip
            label={t('groups.members.selectedCount', { count: selectedUsers.size })}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* Search Field */}
      <TextField
        fullWidth
        placeholder={t('groups.members.searchUsers')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* User List */}
      {usersLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            {searchQuery
              ? t('groups.members.noUsersFound')
              : t('groups.members.noAvailableUsers')
            }
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {filteredUsers.map((user) => (
            <ListItem
              key={user.id}
              disablePadding
              secondaryAction={
                user.selected && (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={user.memberRole}
                      onChange={(e) => handleChangeRole(user.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MenuItem value="member">{t('groups.members.roles.member')}</MenuItem>
                      <MenuItem value="admin">{t('groups.members.roles.admin')}</MenuItem>
                      <MenuItem value="owner">{t('groups.members.roles.owner')}</MenuItem>
                    </Select>
                  </FormControl>
                )
              }
            >
              <ListItemButton onClick={() => handleToggleUser(user.id)}>
                <Checkbox
                  edge="start"
                  checked={user.selected}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemAvatar>
                  <Avatar>{getRoleIcon(user.role ?? 'standard')}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1">{user.full_name || user.username}</Typography>
                      <Chip
                        size="small"
                        label={user.role ?? 'standard'}
                        color={getRoleColor(user.role ?? 'standard')}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={user.email}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* Info Alert */}
      {selectedUsers.size > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('groups.members.willAddUsers', { count: selectedUsers.size })}
        </Alert>
      )}
    </BaseDialog>
  );
};
